import { createDefaultDetailPageTitleFn, DetailPageLayout } from '@app/DetailPageLayout';
import { getAccessMode } from '@common/detailPage.hooks';
import { DetailPageRightMenu } from '@common/detailPageRightMenu/DetailPageRightMenu';
import { EDetailPageRightMenuGeneralInfoType } from '@common/detailPageRightMenu/detailPageRightMenuGeneralInfo.models';
import usePrompt from '@common/prompt.hooks';
import { ESelectRevisionModalType } from '@common/SelectRevisionModal.constants';
import { useSelectRevisionListModal } from '@common/SelectRevisionModal.hooks';
import { ESelectT1CreationModalSearchParamKey } from '@common/SelectT1CreationModal.constants';
import { IProjectCompanyOption } from '@company/projectCompany/projectCompany.models';
import {
  useFetchCheckerCompanyOptions,
  useFetchDesignerCompanyOptions,
} from '@company/projectCompany/projectCompany.queries';
import { useProjectId } from '@config/config.hooks';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { Tab, Tabs } from '@core/Tabs/Tab';
import { useT3CommentPermission, useT3DetailIsReadOnly } from '@designModification/designModification.hooks';
import {
  EDesignModificationFormKeys,
  EDesignModificationIssueRole,
  EDesignModificationStatus,
  IDesignModificationFormState,
  IProjectDesignModification,
} from '@designModification/designModification.models';
import {
  useFetchDesignModification,
  useFetchDesignModificationRevision,
} from '@designModification/designModification.queries';
import { DiscussionList } from '@discussion/DiscussionList';
import { EProjectDiscussionFormType } from '@discussion/projectDiscussion/projectDiscussion.constants';
import { EProjectDiscussionFileType } from '@discussion/projectDiscussion/projectDiscussionFile/projectDiscussionFile.constants';
import { EProjectFileType } from '@file/projectFile/projectFile.constants';
import { IProjectFileBase } from '@file/projectFile/projectFile.models';
import { useFetchAttachmentDesignModification } from '@file/projectFile/projectFile.queries';
import { Badge, ComponentType, convertISOStringToLocalDateString, Form, Scrollbar, useForm } from '@gcl/react-core';
import { IMasterScheduleDetail } from '@masterSchedule/detail/masterScheduleDetail.models';
import { getMasterScheduleDetail } from '@masterSchedule/masterSchedule.hooks';
import { IProjectTeam } from '@projectTeam/projectTeam.models';
import { useFetchTeamsOptionsByCompanyId } from '@projectTeam/projectTeam.queries';
import { IReasonOfChangeBase } from '@reasonOfChange/reasonOfChange.models';
import { useFetchReasonOfChangeOptions } from '@reasonOfChange/reasonOfChange.queries';
import { IRiskCategory } from '@riskCategory/riskCategory.models';
import { useFetchAllRiskCategories } from '@riskCategory/riskCategory.queries';
import { useNavigate } from '@routes/routes.hooks';
import { routes } from '@routes/routes.models';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import './DesignModificationDetailPage.scss';
import { isSuperseded } from '@revision/revision.services';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { isRestrictedAccessFromApiResponse } from '@common/api.services';
import { AxiosErrorResponse } from '@core/error.models';
import { UseQueryResult } from '@tanstack/react-query';
import { DesignModificationDetailTab } from './DesignModificationDetailTab';

export interface IDesignModificationDetailPageProps {}

export const DesignModificationDetailPage: React.FunctionComponent<IDesignModificationDetailPageProps> = ({}) => {
  const { id, mode } = getAccessMode();
  const projectId = useProjectId();
  const { navigate } = useNavigate();
  const [searchParams] = useSearchParams();

  const searchParamMasterScheduleId =
    searchParams.get(ESelectT1CreationModalSearchParamKey.masterScheduleId) !== null
      ? parseInt(searchParams.get(ESelectT1CreationModalSearchParamKey.masterScheduleId) ?? '0', 10)
      : undefined;
  const searchParamIssueRole = searchParams.get(ESelectT1CreationModalSearchParamKey.issueRole) as
    | EDesignModificationIssueRole
    | undefined;

  const useFormMethods = useForm({
    mode: 'onBlur',
    shouldFocusError: true,
  });
  const {
    formState: { isDirty },
  } = useFormMethods;

  const selectedCheckerCompany: IProjectCompanyOption | undefined = useFormMethods.watch(
    EDesignModificationFormKeys.NOMINATED_CHECKER_COMPANY,
  );

  const selectedDesignerCompany: IProjectCompanyOption | undefined = useFormMethods.watch(
    EDesignModificationFormKeys.NOMINATED_DESIGNER_COMPANY,
  );

  const {
    data: designModification,
    isLoading: isLoadingDesignModification,
    isRefetching: isRefetchingDesignModification,
    error: errorGetDesignModification,
  } = useFetchDesignModification({ projectId }, id, !isDirty);

  const {
    data: designModificationRevisions,
    isLoading: isLoadingDesignModificationRevisions,
    isRefetching: isRefetchingDesignModificationRevisions,
    error: errorDesignModificationRevisions,
  } = useFetchDesignModificationRevision({ projectId }, id, !isDirty);

  const {
    masterSchedule,
    isLoading: isLoadingGetMasterSchedule,
    isRefetching: isRefetchingGetMasterSchedule,
    error: errorGetMasterSchedule,
  } = getMasterScheduleDetail(
    projectId,
    designModification?.projectMasterScheduleId ?? searchParamMasterScheduleId,
    !isDirty,
  );

  const { data: checkerCompanyOptions, error: errorCheckerCompanyOptions } = useFetchCheckerCompanyOptions({
    shouldConvertOptions: false,
    isDirty,
  })({
    projectId,
  });

  const { data: checkerTeamOptions, error: errorCheckerTeamOptions } = useFetchTeamsOptionsByCompanyId({
    shouldConvertOptions: false,
  })(
    { projectId, companyId: selectedCheckerCompany?.id ?? designModification?.nominatedCheckerCompany?.id ?? 0 },
    isPositiveNumber(selectedCheckerCompany?.id ?? designModification?.nominatedCheckerCompany?.id),
  );

  const { data: designerCompanyOptions, error: errorDesignerCompanyOptions } = useFetchDesignerCompanyOptions({
    shouldConvertOptions: false,
    isDirty,
  })({
    projectId,
  });

  const { data: designerTeamOptions, error: errorDesignerTeamOptions } = useFetchTeamsOptionsByCompanyId({
    shouldConvertOptions: false,
  })(
    {
      projectId,
      companyId: selectedDesignerCompany?.id ?? designModification?.nominatedDesignerCompany?.id ?? 0,
    },
    isPositiveNumber(selectedDesignerCompany?.id ?? designModification?.nominatedDesignerCompany?.id),
  );

  const { data: dataRiskCategories, error: errorRiskCategories } = (
    useFetchAllRiskCategories({ isDirty }) as () => UseQueryResult<IRiskCategory[], AxiosErrorResponse>
  )();

  const {
    data: reasonOfChangeOptions,
    isLoading: isLoadingReasonOfChangeOptions,
    isRefetching: isRefetchingReasonOfChangeOptions,
    error: errorReasonOfChangeOptions,
  } = useFetchReasonOfChangeOptions({
    isDirty,
  })({
    projectId,
  });

  const { data: attachmentDesignerAttachments, error: errorAttachmentDesignerAttachments } =
    useFetchAttachmentDesignModification(
      {
        projectId,
        designModificationId: id ?? 0,
        fileType: EProjectFileType.T3_DESIGNER,
      },
      false,
    );

  const { data: attachmentDesignerAttachmentsCalculation, error: errorAttachmentDesignerAttachmentsCalculation } =
    useFetchAttachmentDesignModification(
      {
        projectId,
        designModificationId: id ?? 0,
        fileType: EProjectFileType.T3_DESIGNER_CALCULATION,
      },
      false,
    );

  const reasonOfChangeOptionsFiltered = React.useMemo(() => {
    return ((reasonOfChangeOptions ?? []) as IReasonOfChangeBase[])?.filter((r) => r.enabled);
  }, [JSON.stringify(reasonOfChangeOptions)]);

  const getDefaultFormValues = (
    isRefetchingLocal: boolean,
    designModificationLocal?: IProjectDesignModification,
    masterScheduleLocal?: IMasterScheduleDetail,
    reasonOfChangeOptionsLocal?: IReasonOfChangeBase[],
    dataRiskCategoriesLocal?: IRiskCategory[],
    checkerCompanyOptionsLocal?: IProjectCompanyOption[],
    checkerTeamOptionsLocal?: IProjectTeam[],
    designerCompanyOptionsLocal?: IProjectCompanyOption[],
    designerTeamOptionsLocal?: IProjectTeam[],
    designerAttachmentsLocal?: IProjectFileBase[],
    designerAttachmentsCalculationLocal?: IProjectFileBase[],
  ) => {
    if (isRefetchingLocal && useFormMethods.formState.defaultValues) return useFormMethods.formState.defaultValues;

    const riskCat: IRiskCategory | null =
      (mode === 'Create'
        ? masterScheduleLocal?.riskCategory
        : dataRiskCategoriesLocal?.find((c) => designModificationLocal?.riskCategory === c.category)) ?? null;

    const dataReasonOptions = reasonOfChangeOptionsLocal?.filter((r) => {
      return designModification?.changeReasons?.find((selected) => selected.reasonOfChangeId === r.id);
    });

    const selectedDrawings = (designModification?.drawingsModified || []).map((d) => {
      return {
        data: { ...d.drawingRegister, latestRfcHistory: { ...d.drawingRegister.history } },
        key: d.projectDrawingRegisterId,
        text: `${d.drawingRegister.drawingNo} - ${d.drawingRegister.documentDrawingTitle}`,
      };
    });

    const t1CheckerCompany = masterScheduleLocal?.responsibleChecker;
    const nominatedCheckerCompany: IProjectCompanyOption | null =
      (mode === 'Create'
        ? t1CheckerCompany
        : checkerCompanyOptionsLocal?.find((c) => c.id === designModificationLocal?.nominatedCheckerCompany?.id)) ??
      null;

    const t1Checker = masterScheduleLocal?.responsibleCheckerTeams[0];
    const t1NominatedChecker: IProjectTeam | null =
      (mode === 'Create'
        ? ({
            id: t1Checker?.projectTeamId,
            teamName: t1Checker?.teamName,
            projectId: t1Checker?.projectId,
            isDefault: true,
            projectCompanyId: null,
            rowVersion: t1Checker?.rowVersion,
          } as unknown as IProjectTeam)
        : checkerTeamOptionsLocal?.find((c) => c.id === t1Checker?.projectTeamId)) ?? null;
    const nominatedChecker =
      (mode === 'Create' ? t1NominatedChecker : designModificationLocal?.nominatedChecker) ?? null;

    const t1DesignerCompany = masterScheduleLocal?.hasNoDesigner ? null : masterScheduleLocal?.responsibleDesigner;
    const nominatedDesignerCompany: IProjectCompanyOption | null =
      (mode === 'Create'
        ? t1DesignerCompany
        : designerCompanyOptionsLocal?.find((c) => c.id === designModificationLocal?.nominatedDesignerCompany?.id)) ??
      null;

    const t1Designer = masterScheduleLocal?.responsibleDesignerTeams[0];
    const t1NominatedDesigner: IProjectTeam | null =
      (mode === 'Create' ? null : designerTeamOptionsLocal?.find((c) => c.id === t1Designer?.projectTeamId)) ?? null;
    const nominatedDesigner =
      (mode === 'Create' ? t1NominatedDesigner : designModificationLocal?.nominatedDesigner) ?? null;

    const state: IDesignModificationFormState = {
      [EDesignModificationFormKeys.LOCATION]: designModificationLocal?.location ?? null,
      [EDesignModificationFormKeys.ELEMENT_OF_WORKS]: designModificationLocal?.elementOfWorks ?? null,
      [EDesignModificationFormKeys.CHANGE_DETAIL]: designModificationLocal?.changeDetail ?? null,
      [EDesignModificationFormKeys.PROPOSED_MODIFICATION_FILES]:
        designModification?.proposedModificationAttachments ?? [],
      [EDesignModificationFormKeys.REASONS_OF_CHANGE]: dataReasonOptions ?? [],
      [EDesignModificationFormKeys.REQUIRED_APPROVAL_DATE]: designModification?.requiredApprovalDate
        ? convertISOStringToLocalDateString(designModification?.requiredApprovalDate ?? '')
        : '',
      [EDesignModificationFormKeys.RISK_CATEGORY]: riskCat,
      [EDesignModificationFormKeys.NOMINATED_CHECKER_COMPANY]: nominatedCheckerCompany,
      [EDesignModificationFormKeys.NOMINATED_CHECKER]: nominatedChecker,
      [EDesignModificationFormKeys.NOMINATED_DESIGNER_COMPANY]: nominatedDesignerCompany,
      [EDesignModificationFormKeys.NOMINATED_DESIGNER]: nominatedDesigner,
      [EDesignModificationFormKeys.DRAWINGS_MODIFIED]: selectedDrawings,
      [EDesignModificationFormKeys.DESIGNER_COMMENT]: designModification?.designerComment ?? null,
      [EDesignModificationFormKeys.REVIEWER_COMMENT]: designModification?.reviewerComment ?? null,
      [EDesignModificationFormKeys.ICE_COMMENT]: designModification?.iceComment ?? null,
      [EDesignModificationFormKeys.DESIGNER_ATTACHMENTS]: designerAttachmentsLocal ?? [],
      [EDesignModificationFormKeys.DESIGNER_ATTACHMENTS_CALCULATION]: designerAttachmentsCalculationLocal ?? [],
    };

    return state;
  };

  const isRestricted = React.useMemo(() => {
    return isRestrictedAccessFromApiResponse([errorGetDesignModification]);
  }, [JSON.stringify(errorGetDesignModification)]);

  const {
    openPortal,
    renderModal: renderRevisionListModal,
    badge: revisionBadge,
  } = useSelectRevisionListModal({
    type: ESelectRevisionModalType.T3,
    revisions: designModificationRevisions,
  });
  const isSupersededLocal = React.useMemo(() => {
    return isSuperseded(designModificationRevisions ?? []);
  }, [JSON.stringify(designModificationRevisions)]);

  const isReadOnly = useT3DetailIsReadOnly(isSupersededLocal, designModification, masterSchedule);
  const localMode = isReadOnly ? 'View' : mode;

  const isLoading =
    mode === 'Create'
      ? isLoadingGetMasterSchedule || isLoadingReasonOfChangeOptions
      : isLoadingDesignModification ||
        isLoadingDesignModification ||
        isLoadingDesignModificationRevisions ||
        isLoadingReasonOfChangeOptions;
  const isRefetching =
    mode === 'Create'
      ? isRefetchingGetMasterSchedule || isRefetchingReasonOfChangeOptions
      : isRefetchingDesignModification ||
        isRefetchingDesignModificationRevisions ||
        isRefetchingGetMasterSchedule ||
        isRefetchingReasonOfChangeOptions;

  const {
    isDesignerCommentReadOnly,
    isIceCommentReadOnly,
    isReviewerCommentReadOnly,
    hasDesignerCommentCreateRight,
    hasDesignerCommentReplyRight,
    hasIceCommentCreateRight,
    hasIceCommentReplyRight,
    hasReviewerCommentCreateRight,
    hasReviewerCommentReplyRight,
    canViewDesignerDraftComment,
    canViewCheckerDraftComment,
    canViewReviewerDraftComment,
  } = useT3CommentPermission(isSupersededLocal, masterSchedule, designModification);

  return (
    <Scrollbar>
      <ApiErrorDisplay
        errors={[
          { error: errorGetDesignModification, genericError: 'Get design modification failed' },
          { error: errorGetMasterSchedule, genericError: 'Get master schedule failed' },
          { error: errorReasonOfChangeOptions, genericError: 'Get reason of change options failed' },
          { error: errorRiskCategories, genericError: 'Get risk categories failed' },
          { error: errorAttachmentDesignerAttachments, genericError: 'Get designer attachments failed' },
          {
            error: errorAttachmentDesignerAttachmentsCalculation,
            genericError: 'Get designer attachment calculation failed',
          },
          { error: errorCheckerCompanyOptions, genericError: 'Get checker company options failed' },
          { error: errorCheckerTeamOptions, genericError: 'Get checker team options failed' },
          { error: errorDesignModificationRevisions, genericError: 'Get revision failed' },
        ]}
      />
      <DetailPageLayout
        errorGetDetail={errorGetDesignModification}
        isRestricted={isRestricted}
        data={mode === 'Create' ? masterSchedule : designModification && masterSchedule}
        isRefetching={isRefetching}
        isLoading={isLoading}
        rightMenu={(setIsDrawerOpen) => (
          <DetailPageRightMenu
            setIsDrawerOpen={setIsDrawerOpen}
            type={EDetailPageRightMenuGeneralInfoType.T3}
            masterSchedule={masterSchedule}
            designModification={designModification}
          />
        )}
        title={createDefaultDetailPageTitleFn(
          <>
            {`T3 Design Modification ${
              localMode !== 'Create' && designModification?.designChangeNo
                ? ` - ${designModification?.designChangeNo}`
                : ''
            }`}
            {revisionBadge}
            {designModification?.status === EDesignModificationStatus.cancel && (
              <Badge pill type={ComponentType.Secondary} className="gcl-ml-sm gcl-fb-md gcl-fc-greyscale-100 ">
                Cancelled
              </Badge>
            )}
          </>,
        )}
        breadcrumb={[
          {
            text: 'Home',
            prefixIconName: 'House',
            onClick: () => {
              navigate(routes.home.path);
            },
          },
          {
            text: 'Search T3',
            prefixIconName: 'Search',
            onClick: () => {
              navigate(routes.t3.path);
            },
          },
          {
            text: `${localMode} T3`,
            active: true,
            prefixIconName: 'JournalText',
          },
        ]}
        render={() => {
          return (
            <>
              <Tabs>
                <Tab headerText="Details">
                  <Form
                    defaultValues={{
                      callback: () => {
                        const state = getDefaultFormValues(
                          isRefetching,
                          designModification,
                          masterSchedule,
                          reasonOfChangeOptionsFiltered,
                          dataRiskCategories,
                          checkerCompanyOptions?.map((c) => {
                            return { ...c.data, ...c } as IProjectCompanyOption;
                          }),
                          checkerTeamOptions?.map((c) => {
                            return { ...c.data, ...c } as IProjectTeam;
                          }),
                          designerCompanyOptions?.map((c) => {
                            return { ...c.data, ...c } as IProjectCompanyOption;
                          }),
                          designerTeamOptions?.map((c) => {
                            return { ...c.data, ...c } as IProjectTeam;
                          }),
                          attachmentDesignerAttachments,
                          attachmentDesignerAttachmentsCalculation,
                        );
                        return state;
                      },
                      deps: [
                        isRefetching,
                        masterSchedule,
                        designModification,
                        reasonOfChangeOptionsFiltered,
                        dataRiskCategories,
                        checkerCompanyOptions,
                        checkerTeamOptions,
                        designerCompanyOptions,
                        designerTeamOptions,
                        attachmentDesignerAttachments,
                        attachmentDesignerAttachmentsCalculation,
                      ],
                    }}
                    enableUnsavedChangesPrompt
                    unsavedChangesPromptProps={{
                      usePrompt,
                    }}
                    useFormMethods={useFormMethods}
                    render={(formFieldMethods) => {
                      return (
                        <DesignModificationDetailTab
                          useFormMethods={useFormMethods}
                          formFieldMethods={formFieldMethods}
                          isSuperseded={isSupersededLocal}
                          id={Number(id) || undefined}
                          projectId={projectId}
                          masterScheduleId={designModification?.projectMasterScheduleId ?? searchParamMasterScheduleId}
                          masterSchedule={masterSchedule}
                          isReadOnly={isReadOnly}
                          designModification={designModification}
                          mode={localMode}
                          searchParamIssueRole={searchParamIssueRole}
                          checkerCompanyOptions={checkerCompanyOptions?.map((c) => {
                            return { ...c.data, ...c } as IProjectCompanyOption;
                          })}
                          checkerTeamOptions={checkerTeamOptions?.map((c) => {
                            return { ...c.data, ...c } as IProjectTeam;
                          })}
                          designerCompanyOptions={designerCompanyOptions?.map((c) => {
                            return { ...c.data, ...c } as IProjectCompanyOption;
                          })}
                          designerTeamOptions={designerTeamOptions?.map((c) => {
                            return { ...c.data, ...c } as IProjectTeam;
                          })}
                        />
                      );
                    }}
                  />
                </Tab>
                {!!id && (
                  <Tab headerText="Designer Comments">
                    <DiscussionList
                      isReadOnly={isDesignerCommentReadOnly}
                      formType={EProjectDiscussionFormType.DESIGN_MODIFICATION}
                      projectDesignModificationId={id ?? 0}
                      fileFormType={EProjectFileType.DESIGN_MODIFICATION_DISCUSSION_ATTACHMENT}
                      discussionFileType={EProjectDiscussionFileType.DESIGN_MODIFICATION_DESIGNER}
                      canComment={hasDesignerCommentCreateRight}
                      canResponse={hasDesignerCommentReplyRight}
                      defaultType="Designer"
                      canViewDraftComment={canViewDesignerDraftComment}
                    />
                  </Tab>
                )}
                {!!id && (
                  <Tab headerText="Checker Comments">
                    <DiscussionList
                      isReadOnly={isIceCommentReadOnly}
                      formType={EProjectDiscussionFormType.DESIGN_MODIFICATION_ICE}
                      projectDesignModificationId={id ?? 0}
                      fileFormType={EProjectFileType.DESIGN_MODIFICATION_ICE_DISCUSSION_ATTACHMENT}
                      discussionFileType={EProjectDiscussionFileType.DESIGN_MODIFICATION_ICE}
                      canComment={hasIceCommentCreateRight}
                      canResponse={hasIceCommentReplyRight}
                      canViewDraftComment={canViewCheckerDraftComment}
                    />
                  </Tab>
                )}
                {!!id && !!masterSchedule?.responsibleReviewer?.id && (
                  <Tab headerText="Reviewer Comments">
                    <DiscussionList
                      isReadOnly={isReviewerCommentReadOnly}
                      formType={EProjectDiscussionFormType.DESIGN_MODIFICATION_REV}
                      projectDesignModificationId={id ?? 0}
                      fileFormType={EProjectFileType.DESIGN_MODIFICATION_REVIEWER_DISCUSSION_ATTACHMENT}
                      discussionFileType={EProjectDiscussionFileType.DESIGN_MODIFICATION_REVIEWER}
                      canComment={hasReviewerCommentCreateRight}
                      canResponse={hasReviewerCommentReplyRight}
                      canViewDraftComment={canViewReviewerDraftComment}
                    />
                  </Tab>
                )}
              </Tabs>
              {renderRevisionListModal()}
            </>
          );
        }}
      />
    </Scrollbar>
  );
};
